import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from '../../../services/apiClient';

// Fetch all accounts for a canvas
export const fetchAccounts = createAsyncThunk(
  'accounts/fetchAccounts',
  async ({ canvasId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/canvas/${canvasId}/accounts?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Create an account under a canvas
export const createAccount = createAsyncThunk(
  'accounts/createAccount',
  async ({ canvasId, accountData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/canvas/${canvasId}/accounts`,
        accountData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update an account
export const updateAccount = createAsyncThunk(
  'accounts/updateAccount',
  async ({ canvasId, accountId, updateData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(
        `/canvas/${canvasId}/accounts/${accountId}`,
        updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete an account
export const deleteAccount = createAsyncThunk(
  'accounts/deleteAccount',
  async ({ canvasId, accountId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(
        `/canvas/${canvasId}/accounts/${accountId}`);
      return accountId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Upload accounts from file
export const uploadAccounts = createAsyncThunk(
  'accounts/uploadAccounts',
  async ({ canvasId, file }, { rejectWithValue }) => {
    try {
      // Validate file is a proper File object
      if (!(file instanceof File)) {
        throw new Error(`Expected File object but got ${typeof file}: ${JSON.stringify(file)}`);
      }
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `/canvas/${canvasId}/accounts/upload`,
        formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const accountSlice = createSlice({
  name: 'accounts',
  initialState: {
    accounts: [],
    pagination: { total: 0, page: 1, pages: 1 },
    loading: false,
    error: null,
  },
  reducers: {
    resetAccountError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = action.payload.accounts;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pages: action.payload.pages,
        };
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts.push(action.payload);
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAccount.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.accounts.findIndex(acc => acc._id === action.payload._id);
        if (idx !== -1) state.accounts[idx] = action.payload;
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = state.accounts.filter(acc => acc._id !== action.payload);
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadAccounts.fulfilled, (state, action) => {
        state.loading = false;
        //  merge uploaded accounts
        state.accounts = [...state.accounts, ...(action.payload || [])];
      })
      .addCase(uploadAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetAccountError } = accountSlice.actions;
export const selectAccounts = state => state.account.accounts;
export const selectAccountsLoading = state => state.account.loading;
export const selectAccountsPagination = state => state.account.pagination;
export default accountSlice.reducer;